import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import store, { RootState } from "../store";
import { SAVE_QUOTE_RESPONSE, SaveQuoteRequest } from "../api/types";
import { useApi } from "../api";
import {
  lobToCardInfo,
  ProductCardInfo,
} from "../pages/agent-partner-select/agent-partner-select";
import { updateQuote } from "../store/quote/action";
import { usePageLoadingContext } from "../contexts/PageLoadingContext";
import { API_ERROR_MESSAGE } from "../api/launchpad-api";
import { getProductCardColors } from "../config/partner-config/agent-partner-select-constants";
import { v4 as uuidv4 } from 'uuid';
import { getRedirectMessageAvailableCarriers } from "../config/quote-outcome/redirectAlertMessages";
import { isRedirectControl, isSecondPrimaryPill } from "../utils/vendor-filter";
interface AgentPartnerProductCardProps {
  cardInfo?: lobToCardInfo;
  showProducts: boolean;
  setShowAlert?: (show: boolean) => void;
  setAlertMessage?: (message: { title: string; message: string }) => void;
  waitForUserConfirmation?: (partnerInfo: ProductCardInfo) => Promise<void>;
}

export const AgentPartnerProductCard: React.FC<
  AgentPartnerProductCardProps
> = ({
  cardInfo,
  showProducts = true,
  setShowAlert = () => { },
  setAlertMessage = () => { },
  waitForUserConfirmation = () => { },
}) => {
    const {
      mainProductCardColor,
      mainProductCardButtonColor,
      businessClassTextColor,
      disabledTextColor,
      disabledButtonTextColor,
      disabledBackgroundColor,
      copyIconColor,
    } = getProductCardColors(showProducts);

    const api = useApi();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const quote = useSelector((state: RootState) => state.quote.quote);
    const { showLoader, hideLoader } = usePageLoadingContext();
    const previousBusinessKeywordId = useRef<string | null>(null);

    const toggleDropdown = () => {
      setIsDropdownOpen((prev) => !prev);
    };

    const truncateBusinessClass = (maxChars: number, businessClass?: string) => {
      if (!businessClass) return "";
      return businessClass.length > maxChars
        ? businessClass.slice(0, maxChars - 4) + " ..."
        : businessClass;
    };

    const handleButtonClick = async (partnerInfo: ProductCardInfo) => {
      if (partnerInfo.isRedirectable === false) return;
      if (getRedirectMessageAvailableCarriers().includes(partnerInfo.carrier)) {
        try {
          await waitForUserConfirmation(partnerInfo);
        } catch (error) {
          return;
        }
      }
      showLoader();
      let saveQuote: SaveQuoteRequest = {
        ...quote.data,
        CarrierDetail:
          partnerInfo.carrier !== "Geico" &&
            cardInfo?.lobName !== "Cyber Liability"
            ? [
              {
                CarrierCob: partnerInfo.businessClass,
                CarrierCode: partnerInfo.carrier,
                CarrierName: partnerInfo.carrier?.toUpperCase(),
              },
            ]
            : null,
        SelectedProducts: cardInfo?.SelectedProducts ?? [],
      };
      if (
        (quote.data.SelectedProducts[0]?.LobCode !==
          saveQuote.SelectedProducts[0]?.LobCode) ||
        (previousBusinessKeywordId.current !==
          quote.data.BusinessSecondaryKeyword?.id)
      ) {
        const newLaunchpadId = uuidv4();
        quote.data.LaunchpadId = newLaunchpadId;
        saveQuote.LaunchpadId = newLaunchpadId;
        previousBusinessKeywordId.current =
          quote.data.BusinessSecondaryKeyword?.id || null;
      }
      store.dispatch(updateQuote(saveQuote));

      api.commercialAgentApi.saveQuote(saveQuote, true).then(
        (quoteResponse) => {
          if (quoteResponse.type !== SAVE_QUOTE_RESPONSE) {
            window.scrollTo(0, 0);
            setShowAlert(true);
            setAlertMessage({
              title: "Thank you for your request.",
              message: API_ERROR_MESSAGE,
            });
            hideLoader();
            return;
          } else {
            const redirectUrl = quoteResponse.data.redirectUrl;
            if (saveQuote.SelectedProducts[0]?.LobCode === "CAU" ||
              saveQuote.SelectedProducts[0]?.LobCode === "CYBER"
            ) {
              if (!quote.data.CarrierDetail) {
                quote.data.CarrierDetail = [];
              }
              quote.data.CarrierDetail[0] = {
                CarrierCode: quoteResponse.data.carrierCode ? quoteResponse.data.carrierCode : partnerInfo.carrier,
                CarrierName: quoteResponse.data.carrierCode ? quoteResponse.data.carrierCode : partnerInfo.carrier?.toUpperCase(),
                CarrierCob: "Redirect-CAU",
              };
              const updatedQuoteData = {
                ...quote.data,
                CarrierDetail: quote.data.CarrierDetail,
                SelectedProducts: cardInfo?.SelectedProducts ?? [],
              };
              store.dispatch(updateQuote(updatedQuoteData));
            }
            window.open(redirectUrl, "_blank");
            hideLoader();
          }
        },
        (error) => {
          setShowAlert(true);
          setAlertMessage({
            title: "Thank you for your request.",
            message: API_ERROR_MESSAGE,
          });
          hideLoader();
        }
      );
    };

    return (
      <div
        className="agent-partner-product-card-container"
        style={{ backgroundColor: "#fff", border: ".2rem solid transparent" }}
      >
        <div className="agent-partner-product-card-top-row-container">
          <div className="agent-partner-product-card-logo">
            {showProducts ? (
              <>
                <span
                  style={{ color: mainProductCardColor }}
                  {...cardInfo?.logoAttributes}
                ></span>
              </>
            ) : (
              <>
                <div className="agent-partner-product-card-placeholder-logo"></div>
              </>
            )}
          </div>
          <div className="agent-partner-product-card-title-section">
            <p
              className="agent-partner-product-card-title"
              style={{ color: showProducts ? "" : disabledTextColor }}
            >
              {showProducts ? cardInfo?.lobName : "Line Of Business"}
            </p>
            {(cardInfo?.cardInfo[0].carrierBusinessClass || !showProducts) && (
              <div
                className="agent-partner-product-card-business-class-div"
                style={{
                  color: businessClassTextColor,
                  backgroundColor: showProducts
                    ? "#DCE6F6"
                    : disabledBackgroundColor,
                }}
              >
                <p className="agent-partner-product-card-business-class-p">
                  {showProducts
                    ? truncateBusinessClass(30, cardInfo?.cardInfo[0].carrierBusinessClass) ?? ""
                    : "TYPE OF BUSINESS"}
                </p>
                {showProducts && (
                  <span
                    className="geico-icon icon-copy geico-icon--informational geico-icon--xsmall copy-button agent-partner-select-primary-copy-icon"
                    data-keyword="copy paste clipboard"
                    onClick={(e) => {
                      e.preventDefault();
                      navigator.clipboard.writeText(
                        showProducts && cardInfo?.cardInfo[0].carrierBusinessClass
                          ? cardInfo.cardInfo[0].carrierBusinessClass
                          : ""
                      );
                    }}
                    title={`Copy "${showProducts && cardInfo?.cardInfo[0].carrierBusinessClass
                      ? cardInfo.cardInfo[0].carrierBusinessClass
                      : ""
                      }" to clipboard`}
                    style={{
                      color: copyIconColor,
                    }}
                  ></span>
                )}
              </div>
            )}
          </div>
          <div className="agent-partner-product-card-button-section">
            <div className="agent-partner-product-card-partner-buttons">
              {showProducts && !isRedirectControl(cardInfo?.cardInfo[0]?.isRedirectable) ? (
                <span
                  className="agent-partner-product-card-button fit-text"
                  style={{
                    color: mainProductCardButtonColor,
                    cursor: "default",
                    pointerEvents: "none",
                  }}
                >
                  {cardInfo.cardInfo[0].label}
                </span>
              ) : (
              <button
                className="agent-partner-product-card-button fit-text"
                style={{
                  color: showProducts
                    ? mainProductCardButtonColor
                    : disabledButtonTextColor,
                  backgroundColor: showProducts ? "" : mainProductCardButtonColor,
                  border: showProducts ? "" : "none",
                }}
                onClick={() =>
                  cardInfo?.cardInfo[0] && handleButtonClick(cardInfo.cardInfo[0])
                }
                disabled={!showProducts}
              >
                {showProducts
                  ? cardInfo?.cardInfo[0].label
                  : "Partner"}
              </button>
              )}
            </div>
            {(cardInfo?.cardInfo && cardInfo.cardInfo.length > 1) && (
                <button
                  onClick={toggleDropdown}
                  className="agent-partner-select-dropdown-toggle"
                  disabled={!showProducts}
                >
                  <div
                    className="agent-partner-select-other-partners-dropdown"
                    style={{ color: showProducts ? "" : disabledTextColor }}
                  >
                    Other Partners{" "}
                    {
                      <span
                        style={{
                          marginLeft: "5px",
                          color: showProducts
                            ? mainProductCardColor
                            : disabledTextColor,
                        }}
                        className={`geico-icon icon-chevron-${isDropdownOpen ? "up" : "down"
                          } geico-icon--small`}
                      ></span>
                    }
                  </div>
                </button>
              )}
          </div>
        </div>

        <div className="agent-partner-products-card-more-options-container">
          {isDropdownOpen &&
            cardInfo?.cardInfo.slice(1).map((partnerInfo, index) => (
              <React.Fragment key={`partner-${index}`}>
                <div
                  className={
                    isSecondPrimaryPill(index, partnerInfo.isRedirectable)
                      ? "agent-partner-product-card-more-options-button"
                      : "agent-partner-product-card-more-options-button-secondary"
                  }
                  onClick={!isRedirectControl(partnerInfo.isRedirectable) ? undefined : () => handleButtonClick(partnerInfo)}
                  style={!isRedirectControl(partnerInfo.isRedirectable) ? { cursor: "default" } : undefined}
                >
                  <div>
                    <span className="agent-partner-product-card-non-primary-button-text">
                      {`${partnerInfo.label} - ${truncateBusinessClass(
                        80,
                        partnerInfo.carrierBusinessClass
                      )}`}
                    </span>
                    <span
                      className="geico-icon icon-copy geico-icon--informational geico-icon--xsmall copy-button agent-partner-product-card-non-primary-copy-icon"
                      data-keyword="copy paste clipboard"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigator.clipboard.writeText(
                          partnerInfo.carrierBusinessClass
                        );
                      }}
                      title={`Copy "${partnerInfo.carrierBusinessClass}" to clipboard`}
                      style={{ color: copyIconColor }}
                      tabIndex={0}
                    ></span>
                  </div>
                </div>
              </React.Fragment>
            ))}
          {isDropdownOpen && (cardInfo?.cardInfo?.length ?? 0) % 2 == 0 && (
            <div
              className="agent-partner-product-card-more-options-button-secondary"
              style={{ visibility: "hidden" }}
            >
              {`${cardInfo?.cardInfo[cardInfo.cardInfo.length - 1].label
                } - ${truncateBusinessClass(
                  80,
                  cardInfo?.cardInfo[cardInfo.cardInfo.length - 1]
                    .carrierBusinessClass
                )}`}
            </div>
          )}
        </div>
      </div>
    );
  };
